import React, { useEffect, useState } from "react";
import { api, formatDate, formatDateTime, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Settings, Image as ImageIcon, ScrollText, ArrowRight } from "lucide-react";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

export default function StockAdjustmentReview() {
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [decisionMode, setDecisionMode] = useState("approve_all");
  const [ownerNote, setOwnerNote] = useState("");
  const [customItems, setCustomItems] = useState({}); // pid -> {decision, final_qty}
  const [busy, setBusy] = useState(false);

  const loadAll = () => Promise.all([
    api.get("/admin/stock-adjustments").then((r) => setRows(r.data)),
    api.get("/admin/stock-adjustment-history").then((r) => setHistory(r.data)),
    api.get("/admin/stock-adjustments/summary").then((r) => setSummary(r.data)),
  ]);
  useEffect(() => { loadAll(); }, []);

  const openReview = (adj) => {
    setSelected(adj);
    setDecisionMode("approve_all");
    setOwnerNote("");
    const map = {};
    (adj.items || []).forEach((it) => {
      map[it.product_id] = { decision: "approve", final_qty: it.physical_qty };
    });
    setCustomItems(map);
  };

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const payload = { action: decisionMode, owner_note: ownerNote };
      if (decisionMode === "custom") {
        payload.items = Object.entries(customItems).map(([pid, v]) => ({
          product_id: pid, decision: v.decision, final_qty: Number(v.final_qty),
        }));
      }
      const { data } = await api.post(`/admin/stock-adjustments/${selected.id}/decision`, payload);
      if (decisionMode === "note_only") {
        toast.success("Catatan tersimpan — stok tidak diubah, adjustment tetap pending");
        // Refresh dialog dengan data terbaru (untuk lihat riwayat catatan)
        setSelected(data.adjustment);
        setOwnerNote("");
        loadAll();
      } else {
        toast.success(`Keputusan tersimpan: ${data.status.toUpperCase()} · ${data.stock_changes.length} item diaplikasikan`);
        setSelected(null); loadAll();
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const pendingTF = useTableFilter(rows.filter((r) => r.status === "pending"), {
    searchKeys: ["store_name", "submitted_by_name"], defaultSort: { key: "created_at", dir: "desc" },
  });
  const historyTF = useTableFilter(rows.filter((r) => r.status !== "pending"), {
    searchKeys: ["store_name", "status", "owner_note"], defaultSort: { key: "reviewed_at", dir: "desc" },
  });
  const auditTF = useTableFilter(history, {
    searchKeys: ["store_name", "product_name", "type"], defaultSort: { key: "reviewed_at", dir: "desc" },
  });

  const statusBadge = (s) => {
    const map = {
      pending: <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Clock className="w-3 h-3 mr-1"/>Menunggu</Badge>,
      approved: <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1"/>Disetujui</Badge>,
      rejected: <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1"/>Ditolak</Badge>,
      partial: <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Settings className="w-3 h-3 mr-1"/>Parsial</Badge>,
    };
    return map[s] || <Badge>{s}</Badge>;
  };

  return (
    <>
      <PageHeader title="Review Opname Mitra" subtitle="Rekonsiliasi selisih stok fisik vs sistem — approve/reject untuk sinkron kan stok" testId="page-adj-review"/>

      {summary && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-5 border-2 border-amber-300 bg-amber-50/40 rounded-md stat-card">
            <div className="overline mb-2 flex items-center gap-1"><Clock className="w-3 h-3 text-amber-700"/> Menunggu Review</div>
            <div className="font-mono-num text-3xl font-bold text-amber-700">{summary.pending}</div>
          </Card>
          <Card className="p-5 border border-emerald-200 rounded-md stat-card">
            <div className="overline mb-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-700"/> Sudah Diapprove</div>
            <div className="font-mono-num text-3xl font-bold text-emerald-700">{summary.approved}</div>
          </Card>
          <Card className="p-5 border border-red-200 rounded-md stat-card">
            <div className="overline mb-2 flex items-center gap-1"><XCircle className="w-3 h-3 text-red-700"/> Ditolak</div>
            <div className="font-mono-num text-3xl font-bold text-red-700">{summary.rejected}</div>
          </Card>
        </div>
      )}

      <Tabs defaultValue="pending">
        <TabsList className="mb-4">
          <TabsTrigger value="pending" data-testid="tab-pending">Menunggu ({pendingTF.rows.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-decisions">Riwayat Keputusan ({historyTF.rows.length})</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="w-3 h-3 mr-1"/>Audit Trail Stok ({auditTF.rows.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card className="border rounded-md overflow-hidden">
            <div className="p-3 border-b"><TableToolbar tf={pendingTF} placeholder="Cari toko / submitter..."/></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <SortHeader tf={pendingTF} col="opname_date">Tgl Opname</SortHeader>
                <SortHeader tf={pendingTF} col="store_name">Toko</SortHeader>
                <SortHeader tf={pendingTF} col="submitted_by_name">Oleh</SortHeader>
                <TableHead className="text-right">Total Item Selisih</TableHead>
                <TableHead className="text-right">Total Variance</TableHead>
                <TableHead className="w-32 text-right">Aksi</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {pendingTF.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Tidak ada opname yang menunggu review.</TableCell></TableRow>}
                {pendingTF.rows.map((r) => {
                  const totalVar = (r.items || []).reduce((s, it) => s + Number(it.variance || 0), 0);
                  return (
                    <TableRow key={r.id} className="table-row-hover" data-testid={`adj-row-${r.id}`}>
                      <TableCell className="font-mono-num text-sm">{formatDate(r.opname_date)}</TableCell>
                      <TableCell className="font-medium">{r.store_name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{r.submitted_by_name}</TableCell>
                      <TableCell className="text-right font-mono-num">{(r.items || []).length}</TableCell>
                      <TableCell className={`text-right font-mono-num font-semibold ${totalVar === 0 ? "text-slate-400" : totalVar > 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {totalVar > 0 ? "+" : ""}{totalVar}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" onClick={() => openReview(r)} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]" data-testid={`btn-review-${r.id}`}>
                          Review <ArrowRight className="w-3 h-3 ml-1"/>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="border rounded-md overflow-hidden">
            <div className="p-3 border-b"><TableToolbar tf={historyTF} placeholder="Cari toko / status..."/></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <SortHeader tf={historyTF} col="reviewed_at">Waktu Review</SortHeader>
                <SortHeader tf={historyTF} col="store_name">Toko</SortHeader>
                <SortHeader tf={historyTF} col="status">Status</SortHeader>
                <SortHeader tf={historyTF} col="reviewed_by_name">Oleh Owner</SortHeader>
                <TableHead>Catatan</TableHead>
                <TableHead className="w-24 text-right"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {historyTF.rows.map((r) => (
                  <TableRow key={r.id} className="table-row-hover">
                    <TableCell className="text-xs font-mono-num">{r.reviewed_at ? formatDateTime(r.reviewed_at) : "-"}</TableCell>
                    <TableCell className="font-medium">{r.store_name}</TableCell>
                    <TableCell>{statusBadge(r.status)}</TableCell>
                    <TableCell className="text-sm">{r.reviewed_by_name || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-md truncate">{r.owner_note || "-"}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setSelected(r)}>Detail</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <Card className="border rounded-md overflow-hidden">
            <div className="p-3 border-b"><TableToolbar tf={auditTF} placeholder="Cari produk / toko / tipe..."/></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <SortHeader tf={auditTF} col="reviewed_at">Waktu</SortHeader>
                <SortHeader tf={auditTF} col="store_name">Toko</SortHeader>
                <SortHeader tf={auditTF} col="product_name">Produk</SortHeader>
                <SortHeader tf={auditTF} col="system_qty" className="text-right">Sistem</SortHeader>
                <SortHeader tf={auditTF} col="final_qty" className="text-right">Menjadi</SortHeader>
                <SortHeader tf={auditTF} col="variance_applied" className="text-right">Selisih Diterapkan</SortHeader>
                <SortHeader tf={auditTF} col="type">Tipe</SortHeader>
              </TableRow></TableHeader>
              <TableBody>
                {auditTF.rows.map((h) => (
                  <TableRow key={h.id} className="table-row-hover">
                    <TableCell className="text-xs font-mono-num">{formatDateTime(h.reviewed_at)}</TableCell>
                    <TableCell className="text-sm">{h.store_name}</TableCell>
                    <TableCell className="font-medium">{h.product_name}</TableCell>
                    <TableCell className="text-right font-mono-num">{h.system_qty}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold">{h.final_qty}</TableCell>
                    <TableCell className={`text-right font-mono-num font-semibold ${h.variance_applied >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {h.variance_applied >= 0 ? "+" : ""}{h.variance_applied}
                    </TableCell>
                    <TableCell>
                      <Badge className={h.type === "found" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-orange-100 text-orange-700 hover:bg-orange-100"}>
                        {h.type === "found" ? "Ditemukan" : "Susut"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selected?.status === "pending" ? "Review Opname" : "Detail Keputusan"}: {selected?.store_name} · {selected?.opname_date && formatDate(selected.opname_date)}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded text-xs text-slate-600">
                Disubmit oleh <b>{selected.submitted_by_name}</b> pada {formatDateTime(selected.created_at)}
                {selected.notes && <div className="mt-1 italic">Catatan mitra: {selected.notes}</div>}
              </div>

              {selected.photo && (
                <div>
                  <div className="text-xs font-semibold mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3"/> Foto Bukti Fisik:</div>
                  <img src={selected.photo} alt="opname" className="max-h-64 rounded border"/>
                </div>
              )}

              {selected.status === "pending" ? (
                <>
                  <div>
                    <Label>Mode Keputusan</Label>
                    <Select value={decisionMode} onValueChange={setDecisionMode}>
                      <SelectTrigger data-testid="select-decision-mode"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approve_all">✅ Setujui Semua (stok sistem diubah sesuai fisik)</SelectItem>
                        <SelectItem value="reject_all">❌ Tolak Semua (stok sistem tetap, dianggap fisik salah)</SelectItem>
                        <SelectItem value="custom">⚙️ Custom per-item (setujui/tolak + override qty)</SelectItem>
                        <SelectItem value="note_only">💬 Kirim Catatan Saja (stok TIDAK diubah, adjustment tetap pending)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {decisionMode !== "note_only" && (
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead>Produk</TableHead>
                      <TableHead className="text-right">Sistem</TableHead>
                      <TableHead className="text-right">Fisik Dilaporkan</TableHead>
                      <TableHead className="text-right">Selisih</TableHead>
                      {decisionMode === "custom" && <>
                        <TableHead className="w-32">Keputusan</TableHead>
                        <TableHead className="text-right w-28">Qty Final</TableHead>
                      </>}
                    </TableRow></TableHeader>
                    <TableBody>
                      {(selected.items || []).map((it) => (
                        <TableRow key={it.product_id}>
                          <TableCell className="font-medium">{it.product_name}</TableCell>
                          <TableCell className="text-right font-mono-num">{it.system_qty}</TableCell>
                          <TableCell className="text-right font-mono-num">{it.physical_qty}</TableCell>
                          <TableCell className={`text-right font-mono-num font-semibold ${it.variance === 0 ? "text-slate-400" : it.variance > 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {it.variance > 0 ? "+" : ""}{it.variance}
                          </TableCell>
                          {decisionMode === "custom" && <>
                            <TableCell>
                              <Select
                                value={customItems[it.product_id]?.decision || "approve"}
                                onValueChange={(v) => setCustomItems({ ...customItems, [it.product_id]: { ...customItems[it.product_id], decision: v, final_qty: v === "approve" ? it.physical_qty : it.system_qty } })}
                              >
                                <SelectTrigger><SelectValue/></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="approve">Setujui</SelectItem>
                                  <SelectItem value="reject">Tolak</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-right">
                              <Input type="number" className="h-8 text-right font-mono-num"
                                value={customItems[it.product_id]?.final_qty ?? it.physical_qty}
                                onChange={(e) => setCustomItems({ ...customItems, [it.product_id]: { ...customItems[it.product_id], final_qty: e.target.value } })}
                                disabled={customItems[it.product_id]?.decision === "reject"}/>
                            </TableCell>
                          </>}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  )}

                  {/* Riwayat catatan Owner (khusus untuk note_only sebelumnya) */}
                  {(selected.owner_notes_history || []).length > 0 && (
                    <div className="border border-slate-200 rounded-md bg-slate-50/60 p-3">
                      <div className="text-xs font-semibold text-slate-700 mb-2">💬 Riwayat Catatan Supplier ({selected.owner_notes_history.length})</div>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {selected.owner_notes_history.map((n) => (
                          <div key={n.id} className="text-xs bg-white border border-slate-200 rounded p-2">
                            <div className="text-slate-700">{n.note}</div>
                            <div className="text-[10px] text-slate-400 mt-1">— {n.by} · {formatDateTime(n.at)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Catatan Supplier {decisionMode === "note_only" && <span className="text-red-500">*</span>}</Label>
                    <Textarea rows={2} value={ownerNote} onChange={(e) => setOwnerNote(e.target.value)}
                      placeholder={decisionMode === "note_only" ? "Tulis catatan/respon untuk mitra — stok TIDAK akan diubah" : "mis. Setelah verifikasi via foto, fisik memang benar. Susut karena barang rusak minggu lalu."}
                      data-testid="input-owner-note"/>
                  </div>

                  <div className={`p-3 border rounded text-xs ${decisionMode === "note_only" ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-blue-50 border-blue-200 text-blue-800"}`}>
                    {decisionMode === "note_only" ? (
                      <><b>Mode Catatan Saja:</b> Stok sistem <b>tidak diubah</b>, status adjustment tetap <b>pending</b>. Catatan Anda tersimpan di riwayat, dan mitra akan menerima notifikasi. Anda bisa memutuskan approve/reject nanti setelah diskusi selesai.</>
                    ) : (
                      <><b>Info:</b> Setelah keputusan disimpan, stok sistem otomatis di-adjust untuk item yang disetujui, dan mitra akan menerima notifikasi hasil review-nya.</>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Read-only summary */}
                  <div className="p-3 bg-slate-50 rounded">
                    <div className="text-sm"><b>Status:</b> {statusBadge(selected.status)}</div>
                    <div className="text-sm mt-2"><b>Direview oleh:</b> {selected.reviewed_by_name}</div>
                    <div className="text-sm mt-1"><b>Waktu:</b> {selected.reviewed_at && formatDateTime(selected.reviewed_at)}</div>
                    {selected.owner_note && <div className="text-sm mt-2"><b>Catatan Owner:</b> {selected.owner_note}</div>}
                  </div>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead>Produk</TableHead>
                      <TableHead className="text-right">Sistem</TableHead>
                      <TableHead className="text-right">Fisik</TableHead>
                      <TableHead className="text-right">Diputuskan Jadi</TableHead>
                      <TableHead>Keputusan</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(selected.items || []).map((it) => (
                        <TableRow key={it.product_id}>
                          <TableCell className="font-medium">{it.product_name}</TableCell>
                          <TableCell className="text-right font-mono-num">{it.system_qty}</TableCell>
                          <TableCell className="text-right font-mono-num">{it.physical_qty}</TableCell>
                          <TableCell className="text-right font-mono-num font-bold">{it.final_qty}</TableCell>
                          <TableCell>
                            <Badge className={it.decision === "approve" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>
                              {it.decision === "approve" ? "Setujui" : "Tolak"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </div>
          )}
          {selected?.status === "pending" && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelected(null)}>Batal</Button>
              <Button onClick={submit} disabled={busy || (decisionMode === "note_only" && !ownerNote.trim())} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]" data-testid="btn-submit-decision">
                {busy ? "Memproses..." : (decisionMode === "note_only" ? "Kirim Catatan" : "Simpan Keputusan")}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
